// ==========================
// FILE: src/components/Pensions.js
// ==========================
import React, { useEffect, useState, useCallback, useRef } from "react";
import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FiEye, FiUploadCloud, FiSearch, FiRefreshCw } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox";
import { useAccess } from "../access/AccessContext";
import "../styles/layout.css";

// Pension finder directory iframe
// (Confirmed URL)
const PENSION_FINDER_URL = "https://directory.lastwishbox.com/solicitors/";


// Helper to build a blank pension record
function makeEmptyPension() {
  return {
    id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    nickname: "",
    providerName: "",
    pensionType: "",
    policyNumber: "",
    country: "Ireland",
    status: "",
    latestValue: "",
    employerName: "",
    adviserName: "",
    adviserEmail: "",
    notes: "",
  };
}

// Helper: load jsPDF from CDN (text-only PDF, no html2canvas)
async function ensureJsPDF() {
  if (!window.jspdf && !(window.jsPDF && window.jsPDF.jsPDF)) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js";
      s.async = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error("Failed to load jsPDF"));
      document.head.appendChild(s);
    });
  }
  const jsPDF = window.jspdf?.jsPDF || window.jsPDF?.jsPDF;
  if (!jsPDF) throw new Error("jsPDF not available");
  return { jsPDF };
}

// Helper: build plain-text summary for email / PDF
function buildPensionSummaryText({ ownerLabel, pensions, globalNotes, files }) {
  const lines = [];

  if (ownerLabel) {
    lines.push(`Pension Summary for: ${ownerLabel}`, "");
  }

  if (pensions && pensions.length > 0) {
    pensions.forEach((p, idx) => {
      const title =
        p.nickname && p.nickname.trim()
          ? p.nickname.trim()
          : `Pension ${idx + 1}`;
      lines.push(`=== ${title} ===`);
      if (p.providerName) lines.push(`Provider: ${p.providerName}`);
      if (p.policyNumber) lines.push(`Policy / Plan number: ${p.policyNumber}`);
      if (p.pensionType) lines.push(`Type: ${p.pensionType}`);
      if (p.status) lines.push(`Status: ${p.status}`);
      if (p.latestValue) lines.push(`Approx. value: ${p.latestValue}`);
      if (p.employerName) lines.push(`Employer / Scheme: ${p.employerName}`);
      if (p.adviserName) lines.push(`Adviser / Broker: ${p.adviserName}`);
      if (p.adviserEmail) lines.push(`Adviser email: ${p.adviserEmail}`);
      if (p.notes) {
        lines.push("Notes:");
        lines.push(p.notes);
      }
      lines.push(""); // blank line between pensions
    });
  } else {
    lines.push("No pensions have been recorded yet.", "");
  }

  lines.push("=== Overall pension notes ===");
  if (globalNotes && globalNotes.trim()) {
    lines.push(globalNotes.trim(), "");
  } else {
    lines.push("No overall notes have been added yet.", "");
  }

  lines.push("=== Pension documents ===");
  if (files && files.length > 0) {
    files.forEach((f, idx) => {
      lines.push(
        `${idx + 1}. ${f.name}${f.url ? ` – ${f.url}` : ""}`
      );
    });
  } else {
    lines.push("No pension documents uploaded yet.");
  }

  return lines.join("\n");
}

export default function Pensions({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  // ---- main state ----
  const [pensions, setPensions] = useState([]);      // array of pension records
  const [globalNotes, setGlobalNotes] = useState(""); // overall instructions/strategy
  const [files, setFiles] = useState([]);            // [{name,url,time}]

  // ---- extra meta for upload mode ----
  const [signatureName, setSignatureName] = useState("");
  const [signatureDate, setSignatureDate] = useState("");

  // ---- ui state ----
  const [mode, setMode] = useState("choose");        // "choose" | "create" | "upload"
  const [persistedPref, setPersistedPref] = useState(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  // ---- pension finder drawer (iframe) ----
  const [showFinderDrawer, setShowFinderDrawer] = useState(false);
  const [finderLoading, setFinderLoading] = useState(false);
  const [finderIframeKey, setFinderIframeKey] = useState(0);
  const finderIframeRef = useRef(null);

  const [showVideo, setShowVideo] = useState(false);
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [openPensionIds, setOpenPensionIds] = useState([]); // accordion open/closed

  const fileInput = useRef(null);

  const hasExisting =
    (Array.isArray(pensions) && pensions.length > 0) ||
    (Array.isArray(files) && files.length > 0);

  
  // ---- finder drawer helpers ----
  const openFinder = useCallback(() => {
    setFinderLoading(true);
    setShowFinderDrawer(true);
  }, []);

  const resetFinder = useCallback(() => {
    setFinderLoading(true);
    setFinderIframeKey((k) => k + 1);
  }, []);

  const finderDrawerBody = (
    <div className="ew-drawer-iframe-wrap">
      {finderLoading && (
        <div className="ew-directory-loading" aria-live="polite" aria-label="Directory loading">
          <div className="ew-directory-loading__text">Directory loading, please wait…</div>
        </div>
      )}

      <iframe
        key={finderIframeKey}
        ref={finderIframeRef}
        title="Pension Specialist Directory"
        src={PENSION_FINDER_URL}
        className="ew-drawer-iframe"
        onLoad={() => setFinderLoading(false)}
        loading="lazy"
        sandbox="allow-forms allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
      />

      <div className="ew-drawer-iframe-footer">
        <button
          type="button"
          className="btn ew-solicitor-new-search"
          onClick={resetFinder}
          style={{ background: "#f97316", borderColor: "#f97316", color: "#fff" }}
        >
          <FiRefreshCw aria-hidden className="ew-solicitor-return__icon" />
          <span>Start a New Search</span>
        </button>
      </div>
    </div>
  );

  // Pension finder button (matches Secure E‑Will styling/placement)
  const pensionFinderButton = (
    <button
      className="btn btn-main ew-choose__card ew-solicitor-btn"
      type="button"
      onClick={openFinder}
      data-allow-readonly
    >
      <FiSearch className="ew-solicitor-btn__icon" aria-hidden />
      <span>Find a Pension Specialist</span>
    </button>
  );

// ---- load from Firestore on mount ----
useEffect(() => {
  if (!user?.uid) return;

  (async () => {
    try {
      const ref = doc(db, "pensions", user.uid);
      const snap = await getDoc(ref);

      // If nothing saved yet, make sure we reset state
      if (!snap.exists()) {
        setPensions([]);
        setFiles([]);
        setGlobalNotes("");
        setSignatureName("");
        setSignatureDate("");
        setPersistedPref(null);
        setMode("choose");
        setLastSaved(null);
        setOpenPensionIds([]);
        return;
      }

      const d = snap.data() || {};

      const loadedPensions = Array.isArray(d.pensions) ? d.pensions : [];
      const loadedFiles = Array.isArray(d.files) ? d.files : [];
      const loadedNotes = d.globalNotes || "";
      const loadedSigName = d.signatureName || "";
      const loadedSigDate = d.signatureDate || "";

      setPensions(loadedPensions);
      setFiles(loadedFiles);
      setGlobalNotes(loadedNotes);
      setSignatureName(loadedSigName);
      setSignatureDate(loadedSigDate);

      // Decide which mode to show based on saved preference / existing data
      const hasExisting =
        loadedPensions.length > 0 ||
        loadedFiles.length > 0 ||
        loadedNotes.trim().length > 0 ||
        loadedSigName.trim().length > 0 ||
        loadedSigDate.trim().length > 0;

      const pref = d.modePref || null;
      if (pref === "create" || pref === "upload") {
        setPersistedPref(pref);
        setMode(pref);
      } else if (hasExisting) {
        // fall back to create mode if there is data but no explicit pref
        setPersistedPref("create");
        setMode("create");
      } else {
        setPersistedPref(null);
        setMode("choose");
      }

      if (d.updatedAt && typeof d.updatedAt.toDate === "function") {
        setLastSaved(d.updatedAt.toDate());
      } else {
        setLastSaved(null);
      }

      // default: collapse all on load
      setOpenPensionIds([]);
    } catch (err) {
      console.error("Failed to load pensions:", err);
    }
  })();
}, [user?.uid]);


  // ---- validation ----
  function hasCreateValidationErrors() {
    if (!pensions || pensions.length === 0) return false;
    return pensions.some(
      (p) =>
        !p.nickname ||
        !p.nickname.trim() ||
        !p.providerName ||
        !p.providerName.trim()
    );
  }

  // ---- Firestore save ----
  async function handleSave() {
    if (!user?.uid) return;

    if (!readOnly && mode === "create") {
      setValidationTriggered(true);
      if (hasCreateValidationErrors()) {
        alert(
          "Please give each pension a Nickname and Provider before saving.\n\nThis helps your trusted contacts understand which pension is which."
        );
        return;
      }
    }

    setSaving(true);
    setJustSaved(false);

    try {
      const ref = doc(db, "pensions", user.uid);
      const payload = {
        pensions: Array.isArray(pensions) ? pensions : [],
        files: Array.isArray(files) ? files : [],
        globalNotes: globalNotes || "",
        modePref: mode === "create" || mode === "upload" ? mode : "create",
        signatureName,
        signatureDate,
        updatedAt: serverTimestamp(),
      };

      await setDoc(ref, payload, { merge: true });

      setPersistedPref(payload.modePref);
      setLastSaved(new Date());
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (err) {
      alert("Save failed: " + (err?.message || String(err)));
    }
    setSaving(false);
  }

  // ---- upload helpers (multi-file) ----
  const uploadOne = useCallback(
    async (f) => {
      const uid = user?.uid;
      if (!uid) return;
      const path = `pensions/${uid}/${Date.now()}_${f.name}`;
      const sref = storageRef(storage, path);
      const task = uploadBytesResumable(sref, f);
      await new Promise((res, rej) =>
        task.on(
          "state_changed",
          undefined,
          (err) => rej(err),
          () => res()
        )
      );
      const url = await getDownloadURL(task.snapshot.ref);
      const newFile = { name: f.name, url, time: Date.now() };
      const ref = doc(db, "pensions", uid);
      await setDoc(ref, {}, { merge: true });
      await updateDoc(ref, { files: arrayUnion(newFile) });
      setFiles((prev) => [...prev, newFile]);
    },
    [user?.uid]
  );

  const handleFileInputMulti = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length || !user?.uid || readOnly) return;
    setFileUploading(true);
    try {
      for (const f of list) {
        await uploadOne(f);
      }
    } catch (err) {
      alert("Upload failed: " + (err?.message || String(err)));
    }
    setFileUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const onDrop = useCallback(
    async (ev) => {
      ev.preventDefault();
      if (!user?.uid || readOnly) return;
      const list = Array.from(ev.dataTransfer?.files || []);
      if (!list.length) return;
      setFileUploading(true);
      try {
        for (const f of list) await uploadOne(f);
      } catch (err) {
        alert("Upload failed: " + (err?.message || String(err)));
      }
      setFileUploading(false);
    },
    [user?.uid, readOnly, uploadOne]
  );

  const onDragOver = useCallback((ev) => ev.preventDefault(), []);

  async function handleDeleteFile(f) {
    if (!user?.uid || readOnly) return;
    if (!window.confirm(`Remove “${f.name}”?`)) return;
    try {
      const enc = f.url.split("/o/")[1]?.split("?")[0] || "";
      if (enc) {
        await deleteObject(storageRef(storage, decodeURIComponent(enc)));
      }
    } catch {
      // ignore storage delete failure
    }
    try {
      const ref = doc(db, "pensions", user.uid);
      await updateDoc(ref, { files: arrayRemove(f) });
      setFiles((prev) => prev.filter((x) => x.url !== f.url));
    } catch (err) {
      alert("Failed to update file list: " + (err?.message || String(err)));
    }
  }

  // ---- pension list helpers ----
  function handleAddPension() {
    const np = makeEmptyPension();
    setPensions((prev) => [...prev, np]);
    setOpenPensionIds((prev) => [...prev, np.id]); // open new one
  }

  function handleChangePension(id, field, value) {
    setPensions((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  }

  function handleRemovePension(id) {
    if (!window.confirm("Remove this pension from your summary?")) return;
    setPensions((prev) => prev.filter((p) => p.id !== id));
    setOpenPensionIds((prev) => prev.filter((x) => x !== id));
  }

  function togglePensionOpen(id) {
    setOpenPensionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  // ---- derived text ----
  const lastSavedText =
    lastSaved instanceof Date
      ? `Last saved ${lastSaved.toLocaleString()}`
      : null;

  const ChangePrefButton =
    !readOnly && mode !== "choose" ? (
      <div className="ew-prefbar">
        <button
          className="btn btn-danger"
          type="button"
          onClick={() => setMode("choose")}
        >
          Change my pension preferences
        </button>
      </div>
    ) : null;

  // ---- read-only actions: forward + download PDF ----
  const ownerLabel =
    user?.name || user?.displayName || user?.email || "Owner";

  function handleForwardSummary() {
    const summary = buildPensionSummaryText({
      ownerLabel,
      pensions,
      globalNotes,
      files,
    });
    const subject = `Pension summary for ${ownerLabel}`;
    const body = encodeURIComponent(summary);
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${body}`;
  }

  async function handleDownloadPdf() {
    try {
      const { jsPDF } = await ensureJsPDF();
      const docPdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      const summary = buildPensionSummaryText({
        ownerLabel,
        pensions,
        globalNotes,
        files,
      });

      const lines = docPdf.splitTextToSize(
        summary,
        docPdf.internal.pageSize.getWidth() - 80
      );
      let y = 40;
      lines.forEach((line) => {
        if (y > docPdf.internal.pageSize.getHeight() - 40) {
          docPdf.addPage();
          y = 40;
        }
        docPdf.text(line, 40, y);
        y += 16;
      });

      docPdf.save("pensions-summary.pdf");
    } catch (err) {
      alert(
        "PDF export failed. You can still use the email summary.\n" +
          (err?.message || String(err))
      );
    }
  }

  // ---------------------------
  // READ-ONLY SUMMARY VIEW
  // ---------------------------
  const readOnlyView = (
    <>
      <div className="ew-section">
        <h3>Pension summary</h3>
        {(!pensions || pensions.length === 0) && (
          <p>No pensions have been recorded yet.</p>
        )}
        {pensions.map((p) => {
          const isFilled =
            (p.nickname && p.nickname.trim()) ||
            (p.providerName && p.providerName.trim());
          return (
            <div
              key={p.id}
              className={`ew-card pension-card ${
                isFilled ? "pension-card--filled" : "pension-card--empty"
              }`}
            >
              <div className="ew-card-head">
                <div className="ew-card-title">
                  {p.nickname || "Pension"}
                </div>
                <div className="ew-card-subtitle">
                  {p.providerName || "Provider not specified"}
                </div>
              </div>
              <div className="ew-summary-row">
                {p.policyNumber && (
                  <div>
                    <b>Policy number:</b> {p.policyNumber}
                  </div>
                )}
                {p.pensionType && (
                  <div>
                    <b>Type:</b> {p.pensionType}
                  </div>
                )}
                {p.status && (
                  <div>
                    <b>Status:</b> {p.status}
                  </div>
                )}
                {p.latestValue && (
                  <div>
                    <b>Approx. value:</b> {p.latestValue}
                  </div>
                )}
                {p.employerName && (
                  <div>
                    <b>Employer / Scheme:</b> {p.employerName}
                  </div>
                )}
                {p.adviserName && (
                  <div>
                    <b>Adviser:</b> {p.adviserName}
                  </div>
                )}
                {p.adviserEmail && (
                  <div>
                    <b>Adviser email:</b> {p.adviserEmail}
                  </div>
                )}
                {p.notes && (
                  <div>
                    <b>Notes:</b> {p.notes}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="ew-section">
        <h3>Overall pension notes</h3>
        {globalNotes ? (
          <p className="ew-notes">{globalNotes}</p>
        ) : (
          <p>No overall notes have been added yet.</p>
        )}
      </div>

      <div className="ew-section">
        <h3>Pension documents</h3>
        <ul className="ew-files">
          {files && files.length > 0 ? (
            files.map((f) => (
              <li key={f.url} className="ew-file">
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {f.name}
                </a>
              </li>
            ))
          ) : (
            <li className="ew-file ew-file--empty">
              No pension documents uploaded yet.
            </li>
          )}
        </ul>
        {lastSavedText && (
          <div className="ew-last-saved">{lastSavedText}</div>
        )}
      </div>

      {/* Read-only actions */}
      <div className="ew-section ew-save-row">
        <button
          className="btn btn-main"
          type="button"
          onClick={handleDownloadPdf}
        >
          Download PDF
        </button>
        <button
          className="btn"
          type="button"
          onClick={handleForwardSummary}
        >
          Forward by Email
        </button>
      </div>
    </>
  );

  // ---------------------------
  // OWNER EDIT VIEW
  // ---------------------------
  const ownerView = (
    <>
      {ChangePrefButton}

      {/* CHOOSE SCREEN */}
      {mode === "choose" && (
        <div className="ew-choose">
          <div className="ew-choose__grid">
            <button
              className="btn btn-main ew-choose__card"
              onClick={() => setMode("create")}
            >
              Create Pension
            </button>
            <button
              className="btn btn-secondary ew-choose__card"
              onClick={() => setMode("upload")}
            >
              <FiUploadCloud style={{ marginRight: 8 }} />
              Upload Pension Documents
            </button>
          </div>

          {(hasExisting || persistedPref) && (
            <div className="ew-choose__hint">
              You already have saved pension data.{" "}
              <button
                className="linkbtn"
                type="button"
                onClick={() =>
                  setMode(persistedPref === "upload" ? "upload" : "create")
                }
              >
                Continue where you left off ({persistedPref || "create"})
              </button>
            </div>
          )}
        </div>
      )}

      {/* CREATE MODE */}
      {mode === "create" && (
        <div className="ew-main">
          <div className="ew-section">
            <h3>Your pensions</h3>
            {(!pensions || pensions.length === 0) && (
              <p className="ew-empty-hint">
                No pensions added yet. Click &quot;Add pension&quot; to start.
              </p>
            )}

            {pensions.map((p) => {
              const isOpen = openPensionIds.includes(p.id);
              const showNickErr =
                validationTriggered &&
                (!p.nickname || !p.nickname.trim());
              const showProvErr =
                validationTriggered &&
                (!p.providerName || !p.providerName.trim());
              const isFilled =
                (p.nickname && p.nickname.trim()) ||
                (p.providerName && p.providerName.trim());

              return (
                <div
                  key={p.id}
                  className={`ew-card pension-card ${
                    isFilled ? "pension-card--filled" : "pension-card--empty"
                  }`}
                >
                  <div className="pension-card-head">
                    <button
                      type="button"
                      className="pension-card-toggle-btn"
                      onClick={() => togglePensionOpen(p.id)}
                    >
                      <span className="pension-card-title">
                        {p.nickname || "Pension"}
                      </span>
                      <span className="pension-card-provider">
                        {p.providerName || "Provider not set"}
                      </span>
                      <span className="pension-card-toggle-icon">
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger pension-card-remove"
                      onClick={() => handleRemovePension(p.id)}
                      disabled={readOnly}
                    >
                      Remove
                    </button>
                  </div>

                  {isOpen && (
                    <div className="pension-card-body">
                      <div className="ew-grid">
                        <div className="ew-field">
                          <label>
                            Nickname <span className="ew-required">*</span>
                          </label>
                          <input
                            type="text"
                            className={`input ${
                              showNickErr ? "input-error" : ""
                            }`}
                            placeholder="e.g. Old work pension, PRSA with XYZ"
                            value={p.nickname}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "nickname",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                          {showNickErr && (
                            <div className="ew-error">
                              Please add a nickname so this pension is easy to
                              recognise.
                            </div>
                          )}
                        </div>
                        <div className="ew-field">
                          <label>
                            Provider <span className="ew-required">*</span>
                          </label>
                          <input
                            type="text"
                            className={`input ${
                              showProvErr ? "input-error" : ""
                            }`}
                            placeholder="Irish Life, Zurich, Aviva…"
                            value={p.providerName}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "providerName",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                          {showProvErr && (
                            <div className="ew-error">
                              Please add the pension provider.
                            </div>
                          )}
                        </div>
                        <div className="ew-field">
                          <label>Policy / Plan number</label>
                          <input
                            type="text"
                            className="input"
                            value={p.policyNumber}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "policyNumber",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                        </div>
                      </div>

                      <div className="ew-grid">
                        <div className="ew-field">
                          <label>Pension type</label>
                          <input
                            type="text"
                            className="input"
                            placeholder="PRSA, Personal, Occupational, AVC…"
                            value={p.pensionType}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "pensionType",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                        </div>
                        <div className="ew-field">
                          <label>Status</label>
                          <input
                            type="text"
                            className="input"
                            placeholder="Active, Paid-up, In payment…"
                            value={p.status}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "status",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                        </div>
                        <div className="ew-field">
                          <label>Latest value (approx.)</label>
                          <input
                            type="text"
                            className="input"
                            placeholder="e.g. €75,000"
                            value={p.latestValue}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "latestValue",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                        </div>
                      </div>

                      <div className="ew-grid">
                        <div className="ew-field">
                          <label>Employer / Scheme</label>
                          <input
                            type="text"
                            className="input"
                            placeholder="If it came from an employer scheme"
                            value={p.employerName}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "employerName",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                        </div>
                        <div className="ew-field">
                          <label>Adviser / Broker</label>
                          <input
                            type="text"
                            className="input"
                            value={p.adviserName}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "adviserName",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                        </div>
                        <div className="ew-field">
                          <label>Adviser email</label>
                          <input
                            type="email"
                            className="input"
                            value={p.adviserEmail}
                            onChange={(e) =>
                              handleChangePension(
                                p.id,
                                "adviserEmail",
                                e.target.value
                              )
                            }
                            disabled={readOnly}
                          />
                        </div>
                      </div>

                      <div className="ew-field">
                        <label>Notes / special instructions</label>
                        <textarea
                          className="input"
                          rows={3}
                          placeholder="Anything important about this pension – beneficiaries, special terms, where the paperwork is kept, etc."
                          value={p.notes}
                          onChange={(e) =>
                            handleChangePension(
                              p.id,
                              "notes",
                              e.target.value
                            )
                          }
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              className="btn btn-main"
              onClick={handleAddPension}
              disabled={readOnly}
            >
              Add pension
            </button>
          </div>

          <div className="ew-section">
            <h3>Overall pension notes</h3>
            <textarea
              className="input"
              rows={4}
              value={globalNotes}
              onChange={(e) => setGlobalNotes(e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="ew-section ew-save-row">
            <button
              className="btn btn-main"
              type="button"
              onClick={handleSave}
              disabled={saving || readOnly}
            >
              {saving ? "Saving…" : "Save pensions"}
            </button>
            {justSaved && <span className="ew-saved-pill">Saved!</span>}
            {lastSavedText && (
              <span className="ew-last-saved">{lastSavedText}</span>
            )}
          </div>
        </div>
      )}

      {/* UPLOAD MODE – MATCHES E-WILL LAYOUT */}
      {mode === "upload" && (
        <div className="ew-card">
          <div
            className="ew-dropzone"
            onDrop={onDrop}
            onDragOver={onDragOver}
            role="button"
            aria-label="Drag and drop pension files here"
          >
            <FiUploadCloud className="ew-dz-ico" />
            <div className="ew-dz-title">Drop your pension files here</div>
            <div className="ew-dz-sub">
              PDF, DOCX, images. You can add multiple files.
            </div>
            <div className="ew-dz-actions">
              <label className="btn btn-secondary">
                Browse files
                <input
                  type="file"
                  multiple
                  onChange={handleFileInputMulti}
                  style={{ display: "none" }}
                  disabled={fileUploading || readOnly}
                  ref={fileInput}
                />
              </label>
            </div>
            {fileUploading && (
              <div className="ew-uploading">Uploading…</div>
            )}
          </div>

          {!!files.length && (
            <ul className="ew-files" style={{ marginTop: 12 }}>
              {files.map((f) => (
                <li key={f.url} className="ew-file">
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {f.name}
                  </a>
                  {!readOnly && (
                    <button
                      className="btn btn-danger"
                      type="button"
                      onClick={() => handleDeleteFile(f)}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="ew-section" style={{ marginTop: 16 }}>
            <h3>Signatory &amp; Date</h3>
            <div className="grid2">
              <div>
                <label className="label">Your full name (signature)</label>
                <input
                  className="input"
                  placeholder="Type your full name"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  disabled={readOnly}
                />
              </div>
              <div>
                <label className="label">Date of signing</label>
                <input
                  className="input"
                  type="date"
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          <div className="ew-toolbar" style={{ marginTop: 12 }}>
            <button
              className="btn"
              type="button"
              onClick={() => setMode("choose")}
              disabled={saving}
            >
              Back
            </button>
            <button
              className="btn btn-main"
              type="button"
              onClick={handleSave}
              disabled={saving || readOnly || files.length === 0}
            >
              {saving ? "Saving…" : "Save Uploaded Pensions"}
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="ew-root">
      <PageScaffold
        title="Pensions (Secure Pension Vault)"
        heroSrc="/lastwish-Pension.jpg"
        heroAlt="Pensions"
        onAdd={undefined}
        afterHero={pensionFinderButton}
        actionsDesktop={
          <div className="dp-help-row">
            <button
              className="btn dp-help read"
              onClick={() => setShowDrawer(true)}
              data-allow-readonly
            >
              <FiEye className="dp-help-ico" /> Read how it works
            </button>
            <button
              className="btn dp-help see"
              onClick={() => setShowVideo(true)}
              data-allow-readonly
            >
              <FaYoutube className="dp-help-ico" /> See how it works
            </button>
          </div>
        }
        actionsMobile={
          <div className="dp-help-row">
            <button
              className="btn dp-help read"
              onClick={() => setShowDrawer(true)}
              data-allow-readonly
            >
              <FiEye className="dp-help-ico" /> Read how it works
            </button>
            <button
              className="btn dp-help see"
              onClick={() => setShowVideo(true)}
              data-allow-readonly
            >
              <FaYoutube className="dp-help-ico" /> See how it works
            </button>
          </div>
        }
      >
        {readOnly ? readOnlyView : ownerView}
      </PageScaffold>

      {/* Help video */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        title="How the Pensions page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* Help drawer */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside
            className="dp-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">
                How the Pensions page works
              </div>
              <div className="dp-drawer-tools">
                <button
                  className="btn btn-cancel"
                  type="button"
                  onClick={() => setShowDrawer(false)}
                  data-allow-readonly
                >
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>
                This page acts as a secure <b>pension vault</b>. The user can
                either <b>create a structured summary</b> of all their pensions
                or <b>upload pension documents</b> – or both.
              </p>
              <p>
                In <b>Create Pension Summary</b>, you record the key details for
                each pension: provider, policy number, status, approximate
                value, and adviser contacts. This gives loved ones and
                professionals a clear map of everything that exists.
              </p>
              <p>
                In <b>Upload Pension Documents</b>, you store PDFs and other
                files like policy documents and annual statements. These files
                are uploaded to secure storage linked to the user&apos;s
                account.
              </p>
              <p>
                Nothing here moves money or gives investment advice – it&apos;s
                purely about <b>organisation and clarity</b>, so the right
                people can quickly understand the user&apos;s pensions when
                needed.
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* Pension specialist directory drawer */}
      {showFinderDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowFinderDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">Find a Pension Specialist</div>
              <div className="dp-drawer-tools">
                <button
                  className="btn btn-cancel"
                  type="button"
                  onClick={() => setShowFinderDrawer(false)}
                  data-allow-readonly
                >
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body dp-drawer-body--directory">{finderDrawerBody}</div>
          </aside>
        </div>
      )}

      {/* Styles */}
      <style>{`
        :root {
          --topbar-h:56px;
          --ink:#2a0516;
          --muted:#6b7280;
          --b:#ececf5;
          --b2:#d6d6ea;
        }

        .dp-help-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .dp-help {
          border-radius: 8px;
          padding: 10px 14px;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          color: #fff;
          border: none;
          cursor: pointer;
        }
        .dp-help.read { background: #650202; }
        .dp-help.see { background: #003353; }
        .dp-help-ico { flex-shrink: 0; }

        .dp-drawer-bg{ position:fixed; inset:0; background:rgba(0,0,0,.3); z-index:2147483000; display:flex; justify-content:flex-end; }
        .dp-drawer{
          position:fixed; top:var(--topbar-h, 56px); right:0; height:calc(100vh - var(--topbar-h, 56px));
          width:min(540px,95vw); background:#fff; box-shadow:-8px 0 24px rgba(0,0,0,.15);
          border-top-left-radius:12px; border-bottom-left-radius:12px; display:flex; flex-direction:column; overflow:hidden;
          animation:dp-slide-in .22s ease-out both; z-index:2147483001;
        }
        @media (min-width: 768px){
          .dp-drawer{ width:min(700px,90vw); }
        }
        @media (min-width: 1200px){
          .dp-drawer{ width:75vw; }
        }
        @keyframes dp-slide-in {
          from { transform: translateX(20px); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
        .dp-drawer-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title {
          font-weight: 700;
          font-size: 1rem;
        }
        .dp-drawer-body{ padding:16px; overflow:auto; flex:1; min-height:0; padding-bottom:max(16px, env(safe-area-inset-bottom)); }
        
        .dp-drawer-body--directory{
          padding: 0 !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
          min-height: 0 !important;
        }

        /* Pension finder button – match Secure E‑Will */
        .ew-solicitor-btn{ width:100%; background:#f97316; border-color:#f97316; display:inline-flex; align-items:center; justify-content:center; gap:10px; text-transform:none; }
        .ew-solicitor-btn__icon{ font-size:1.1rem; }

        /* Directory iframe wrapper (fills drawer height) */
        .ew-drawer-iframe-wrap{
          position: relative;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
        }
        .ew-drawer-iframe{
          width: 100%;
          border: 0;
          flex: 1;
          min-height: 0;
          height: 100%;
        }
        .ew-drawer-iframe-footer{
          flex: 0 0 auto;
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
          background: #fff;
          border-top: 1px solid #eee;
          padding: 10px 12px;
          padding-bottom: max(12px, env(safe-area-inset-bottom));
        }
        .ew-solicitor-new-search{
          flex: 1 1 200px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        .ew-solicitor-return__icon{ font-size: 1.1rem; }

        .ew-directory-loading{
          position: absolute;
          inset: 0;
          display: grid;
          place-items: center;
          background: rgba(255,255,255,.75);
          backdrop-filter: saturate(120%) blur(1px);
          z-index: 2;
          pointer-events: none;
        }
        .ew-directory-loading__text{
          font-weight: 900;
          color: #980000;
          letter-spacing: .2px;
          animation: ewPulse 1.2s ease-in-out infinite;
        }
        @keyframes ewPulse{
          0%,100%{opacity:.35;transform:scale(.98)}
          50%{opacity:1;transform:scale(1)}
        }

        /* Mobile: keep drawers above fixed footer (matches Secure E‑Will) */
        @media (max-width:700px){
          .dp-drawer-bg{ align-items:flex-end; }
          .dp-drawer{ top:auto !important; height:75vh !important; bottom:calc(84px + env(safe-area-inset-bottom)) !important; }
        }
.dp-drawer-body p + p {
          margin-top: 0.75rem;
        }

        .ew-required { color: #e53935; margin-left: 2px; }
        .input-error { border-color: #e53935; }
        .ew-error { color: #e53935; font-size: 0.85rem; margin-top: 0.25rem; }

        .ew-choose {
          background:#fff;
          border:1px solid var(--b);
          border-radius:14px;
          padding:18px;
          margin-top:14px;
        }
        .ew-choose__grid {
          display:grid;
          grid-template-columns:1fr;
          gap:12px;
        }
        @media(min-width:700px){
          .ew-choose__grid { grid-template-columns:1fr 1fr; }
        }
        .ew-choose__card {
          padding:18px 20px;
          border-radius:12px;
          font-weight:700;
          justify-content:center;
          display:inline-flex;
          align-items:center;
        }
        .ew-choose__hint {
          margin-top:12px;
          color:#4b5563;
        }
        .linkbtn {
          background:none;
          border:none;
          padding:0;
          color:#003353;
          text-decoration:underline;
          cursor:pointer;
        }

        .ew-card{
          background:#fff;
          border:1px solid var(--b);
          border-radius:14px;
          padding:16px;
          margin-top:14px;
        }
        .ew-section{
          background:#fff;
          border:1px solid var(--b);
          border-radius:12px;
          padding:12px;
          margin-top:14px;
        }

        .ew-dropzone{
          border:2px dashed var(--b2);
          border-radius:14px;
          padding:28px;
          text-align:center;
          background:#fafafa;
        }
        .ew-dz-ico{ font-size:36px; opacity:.9; }
        .ew-dz-title{ font-weight:800; color:#0f172a; margin-top:8px; }
        .ew-dz-sub{ color:#6b7280; margin-top:4px; }
        .ew-dz-actions{ margin-top:10px; }
        .ew-uploading{ margin-top:10px; color:#6b7280; }

        .label{
          display:block;
          font-size:13px;
          color:#6b7280;
          font-weight:600;
          margin:6px 0 4px;
        }
        .input{
          width:100%;
          min-height:42px;
          padding:10px 12px;
          border:1px solid var(--b2);
          border-radius:10px;
          background:#fff;
          color:var(--ink);
          outline:none;
          transition:border-color .15s, box-shadow .15s;
        }
        .input:focus{
          border-color:#2a0516;
          box-shadow:0 0 0 3px rgba(42,5,22,.12);
        }

        .grid2{ display:grid; grid-template-columns:1fr; gap:10px; }
        @media(min-width:700px){ .grid2{ grid-template-columns:1fr 1fr; } }

        .ew-grid{ display:grid; grid-template-columns:1fr; gap:16px; }
        @media(min-width:900px){ .ew-grid{ grid-template-columns:1fr 1fr; } }

        .ew-files{ list-style:none; padding:0; margin:10px 0 0; }
        .ew-file{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          padding:8px 10px;
          border:1px solid var(--b);
          border-radius:10px;
          background:#fafafa;
        }
        .ew-file--empty{
          justify-content:flex-start;
          color:#6b7280;
        }

        .ew-save-row{
          display:flex;
          align-items:center;
          gap:8px;
          flex-wrap:wrap;
        }
        .ew-saved-pill{
          background:#16a34a;
          color:#fff;
          border-radius:999px;
          padding:4px 10px;
          font-size:12px;
        }

        .ew-last-saved{
          font-size:12px;
          color:#6b7280;
        }

        .ew-toolbar{
          display:flex;
          gap:8px;
          flex-wrap:wrap;
          align-items:center;
        }

        /* Pension tiles highlighting */
        .pension-card {
          transition: border-color .15s, box-shadow .15s, background-color .15s;
        }
        .pension-card--filled {
          background:#f0f6fb;
          border-color:#003353;
          box-shadow:0 4px 12px rgba(0,0,0,.04);
        }
        .pension-card--filled .pension-card-title {
          color:#003353;
        }

        /* Accordion styling */
        .pension-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 4px;
        }
        .pension-card-toggle-btn {
          flex: 1;
          border: none;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 0;
          cursor: pointer;
          text-align: left;
        }
        .pension-card-title {
          font-weight: 600;
        }
        .pension-card-provider {
          font-size: 0.9rem;
          color: #6b7280;
          flex: 1;
          text-align: left;
        }
        .pension-card-toggle-icon {
          font-size: 1.4rem;
          line-height: 1;
          padding: 0 4px;
        }
        .pension-card-remove {
          white-space: nowrap;
        }
        .pension-card-body {
          margin-top: 10px;
          border-top: 1px solid #ececf5;
          padding-top: 10px;
        }
      `}</style>
    </div>
  );
}
